import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { GoogleLogin } from '@react-oauth/google';
import ReCAPTCHA from 'react-google-recaptcha';
import { login, signUp, registerBusiness, googleLogin } from '../../services/auth';
import { getAllFacilityTypes } from '../../services/facility';
import { environment } from '../../http';
import WebsiteNavbar from '../../components/WebsiteUI/WebsiteNavbar';
import { usStates, decrypt } from '../../utils';
import { RECAPTCHA_SITE_KEY_ENC } from '../../constants';
import './AuthPage.css';

// Try decrypt first; fall back to raw key if decrypt fails (e.g. wrong encryption secret)
let RECAPTCHA_SITE_KEY;
try {
  RECAPTCHA_SITE_KEY = decrypt(RECAPTCHA_SITE_KEY_ENC);
  if (!RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY.length < 10) throw new Error('bad decrypt');
} catch {
  RECAPTCHA_SITE_KEY = '6LdtbZcsAAAAANzSiAuZqwotIRsyGa9hhGURcT00';
}

const AuthPage = ({ defaultView = 'login' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromVerification = location.state?.token;
  const fromDemo = location.state?.fromDemo;
  const [view, setView] = useState(defaultView);
  const [step, setStep] = useState(fromVerification ? 2 : 1);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [locationStatus, setLocationStatus] = useState(null);
  const recaptchaRef = useRef(null);
  const { allFacilityTypes } = useSelector((state) => state.facility);
  const facilityTypeOptions = allFacilityTypes?.ftypes || [];

  useEffect(() => {
    if (step === 3) {
      getAllFacilityTypes();
    }
  }, [step]);

  // Reset captcha whenever view switches so stale tokens don't carry over
  useEffect(() => {
    recaptchaRef.current?.reset();
    setCaptchaToken(null);
  }, [view]);

  // When login() re-navigates to /signup (same path, new state), reset to business-form step.
  // Without this, the component doesn't remount on same-route navigation and view/step stay stale.
  useEffect(() => {
    if (location.state?.token) {
      setView('signup');
      setStep(2);
      setErrors({});
      setPendingVerification(false);
    }
  }, [location.key]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    adminPhone: '',
    confirmPassword: '',
    businessName: location.state?.businessName || '',
    businessEmail: '',
    businessPhone: '',
    facilityName: '',
    facilityType: '',
    facilityAddress: '',
    facilityApt: '',
    facilityZip: '',
    facilityState: '',
    skipFacility: false,
    rememberMe: false,
    smsOptIn: false,
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  useEffect(() => {
    if (location.state?.token) {
      setFormData((prev) => ({
        ...prev,
        businessName: location.state.businessName,
      }));
    }
  }, [location.state?.token]);

  const validateLoginFields = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Please enter a valid email';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!captchaToken && environment !== 'local') newErrors.captcha = 'Please complete the CAPTCHA';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Please enter a valid email';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 8)
      newErrors.password = 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = 'Passwords do not match';
    if (!captchaToken && environment !== 'local') newErrors.captcha = 'Please complete the CAPTCHA';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.businessName.trim()) newErrors.businessName = 'Business name is required';
    if (!formData.businessEmail.trim()) newErrors.businessEmail = 'Business email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.businessEmail))
      newErrors.businessEmail = 'Please enter a valid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    if (formData.skipFacility) return true;
    const newErrors = {};
    if (!formData.facilityName.trim()) newErrors.facilityName = 'Facility name is required';
    if (!formData.facilityType) newErrors.facilityType = 'Please select a type';
    if (!formData.facilityAddress.trim()) newErrors.facilityAddress = 'Address is required';
    if (!formData.facilityZip.trim()) newErrors.facilityZip = 'Zip code is required';
    if (!formData.facilityState) newErrors.facilityState = 'Please select a state';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateLoginFields()) return;
    setIsSubmitting(true);
    setLocationStatus(null);
    try {
      await login({ email: formData.email, password: formData.password, captchaToken }, navigate, {
        onLocationStatus: (status) => setLocationStatus(status),
      });
    } catch (err) {
      // login() handles error display via swal
    } finally {
      setIsSubmitting(false);
      // Reset captcha after every attempt so user must re-verify on retry
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      await googleLogin({
        client_id: credentialResponse?.clientId,
        jwtToken: credentialResponse?.credential,
      }, navigate);
    } catch (err) {
      // handled by service
    }
  };

  const handleNextStep = async () => {
    if (step === 1) {
      if (!validateStep1()) return;
      setIsSubmitting(true);
      try {
        const res = await signUp({
          name: `${formData.firstName} ${formData.lastName}`,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.adminPhone,
          email: formData.email,
          password: formData.password,
          repassword: formData.confirmPassword,
          captchaToken,
          smsOptIn: formData.smsOptIn,
        });
        if (res?.status === 200) {
          setPendingVerification(true);
          setErrors({});
        }
      } catch (err) {
        // signUp handles error display
      } finally {
        setIsSubmitting(false);
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      }
    } else if (step === 2) {
      if (!validateStep2()) return;
      setIsSubmitting(true);
      try {
        const res = await registerBusiness({
          companyName: formData.businessName,
          businessEmail: formData.businessEmail,
          contactPhone: formData.businessPhone,
        }, navigate);
        if (res) {
          setStep(3);
          setErrors({});
        }
      } catch (err) {
        // handled by service
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCreateAccount = async () => {
    if (!validateStep3()) return;
    setIsSubmitting(true);
    try {
      const destination = fromDemo ? '/selectprice?fromDemo=true' : '/selectprice';
      if (formData.skipFacility) {
        navigate(destination);
      } else {
        // TODO: Wire to facility creation service when available
        navigate(destination);
      }
    } catch (err) {
      // handled by service
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrevStep = () => {
    if (step === 1 || (step === 2 && fromVerification)) {
      setView('login');
      setStep(1);
      setErrors({});
      setPendingVerification(false);
      navigate('/login', { replace: true });
    } else {
      setStep((prev) => prev - 1);
      setErrors({});
    }
  };

  const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );

  const EyeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

  const ChevronDownIcon = () => (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 1L5 5L9 1" />
    </svg>
  );

  return (
    <>
    <WebsiteNavbar />
    <main className="auth-page">
      <div className="auth-page__container">
        <div className="auth-page__image">
          <img src="/assets/website/onboarding/services.png" alt="Ogiso Platform" />
        </div>
        <div className="auth-page__content">
          <div className="auth-form">
            {view === 'login' && (
              <div className="auth-form__view">
                <div className="auth-form__header">
                  <h2 className="auth-form__title">Welcome Back</h2>
                  <p className="auth-form__subtitle">Please enter your details to login.</p>
                </div>
                <form onSubmit={handleLoginSubmit} className="auth-form__form">
                  <div className="auth-form__field">
                    <label htmlFor="email" className="auth-form__label">Email Address</label>
                    <input type="email" id="email" name="email" placeholder="john@example.com" value={formData.email} onChange={handleChange} autoComplete="email" className={`auth-form__input ${errors.email ? 'auth-form__input--error' : ''}`} />
                    {errors.email && <span className="auth-form__error">{errors.email}</span>}
                  </div>
                  <div className="auth-form__field">
                    <label htmlFor="password" className="auth-form__label">Password</label>
                    <div className="auth-form__input-wrapper">
                      <input type={showPassword ? 'text' : 'password'} id="password" name="password" placeholder="••••••••" value={formData.password} onChange={handleChange} autoComplete="new-password" className={`auth-form__input ${errors.password ? 'auth-form__input--error' : ''}`} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="auth-form__toggle-password">
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                    </div>
                    {errors.password && <span className="auth-form__error">{errors.password}</span>}
                  </div>
                  <div className="auth-form__options">
                    <label className="auth-form__checkbox-label">
                      <div className="auth-form__checkbox-wrapper">
                        <input type="checkbox" name="rememberMe" checked={formData.rememberMe} onChange={handleChange} className="auth-form__checkbox" />
                        <svg className="auth-form__checkbox-icon" viewBox="0 0 12 10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1.5 5 4.5 8 10.5 2" /></svg>
                      </div>
                      <span className="auth-form__checkbox-text">Remember me</span>
                    </label>
                    <Link to="/forget-password" className="auth-form__link">Forgot Password?</Link>
                  </div>
                  {environment !== 'local' && (
                    <div className="auth-form__captcha">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(token) => { setCaptchaToken(token); setErrors((prev) => ({ ...prev, captcha: '' })); }}
                        onExpired={() => setCaptchaToken(null)}
                      />
                      {errors.captcha && <span className="auth-form__error">{errors.captcha}</span>}
                    </div>
                  )}
                  {locationStatus && (
                    <div className={`auth-form__location-status auth-form__location-status--${locationStatus.status}`}>
                      {(locationStatus.status === 'acquiring' || locationStatus.status === 'verifying') && (
                        <div className="auth-form__location-checking">
                          <span className="auth-form__spinner" />
                          <span>{locationStatus.message}</span>
                        </div>
                      )}
                      {locationStatus.status === 'denied' && (
                        <div className="auth-form__location-denied">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B42318" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                          </svg>
                          <div><strong>Login restricted by location</strong><p>{locationStatus.message}</p></div>
                        </div>
                      )}
                      {locationStatus.status === 'error' && (
                        <div className="auth-form__location-error">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                          </svg>
                          <div><strong>Location required</strong><p>{locationStatus.message}</p></div>
                        </div>
                      )}
                      {locationStatus.status === 'success' && (
                        <div className="auth-form__location-success">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#027A48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                          <span>{locationStatus.message}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <button type="submit" disabled={isSubmitting} className="auth-form__submit">
                    {isSubmitting && !locationStatus ? <><span className="auth-form__spinner" />Logging in...</> : locationStatus?.status === 'denied' || locationStatus?.status === 'error' ? 'Try Again' : 'Login'}
                  </button>
                  <div className="auth-form__divider"><span>OR</span></div>
                  <div className="auth-form__google-wrapper">
                    <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => {}} size="large" text="" logo_alignment="center" shape="rectangular" />
                  </div>
                  {/* <p className="auth-form__switch">
                    Don't have an account?{' '}
                    <button type="button" onClick={() => { setView('signup'); setStep(1); setErrors({}); }} className="auth-form__switch-link">Sign up</button>
                  </p> */}
                </form>
              </div>
            )}

            {view === 'signup' && pendingVerification && (
              <div className="auth-form__view">
                <div className="auth-form__header">
                  <div className="auth-form__verify-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ffb700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" />
                      <path d="M22 6L12 13L2 6" />
                    </svg>
                  </div>
                  <h2 className="auth-form__title">Verify Your Email</h2>
                  <p className="auth-form__subtitle">
                    We've sent a verification link to <strong>{formData.email}</strong>. Please check your inbox and click "Confirm My Account" to continue.
                  </p>
                </div>
                <div className="auth-form__verify-actions">
                  <button
                    type="button"
                    onClick={() => { setPendingVerification(false); setView('login'); setStep(1); }}
                    className="auth-form__submit"
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            )}

            {view === 'signup' && !pendingVerification && (
              <div className="auth-form__view">
                <div className="auth-form__header">
                  <span className="auth-form__step-label">Step {step}</span>
                  <h2 className="auth-form__title">
                    {step === 1 && 'Create Admin Profile'}
                    {step === 2 && 'Create Business Profile'}
                    {step === 3 && 'Facility Setup'}
                  </h2>
                  <p className="auth-form__subtitle">
                    {step === 1 && "Let's get you all set up so you can access your personal account."}
                    {step === 2 && "Let's get your business profile setup."}
                    {step === 3 && 'Tell us about your facility location.'}
                  </p>
                </div>
                <form className="auth-form__form" onSubmit={(e) => e.preventDefault()}>
                  {step === 1 && (
                    <>
                      <div className="auth-form__row">
                        <div className="auth-form__field">
                          <label htmlFor="firstName" className="auth-form__label">First Name</label>
                          <input type="text" id="firstName" name="firstName" placeholder="John" value={formData.firstName} onChange={handleChange} className={`auth-form__input ${errors.firstName ? 'auth-form__input--error' : ''}`} />
                          {errors.firstName && <span className="auth-form__error">{errors.firstName}</span>}
                        </div>
                        <div className="auth-form__field">
                          <label htmlFor="lastName" className="auth-form__label">Last Name</label>
                          <input type="text" id="lastName" name="lastName" placeholder="Doe" value={formData.lastName} onChange={handleChange} className={`auth-form__input ${errors.lastName ? 'auth-form__input--error' : ''}`} />
                          {errors.lastName && <span className="auth-form__error">{errors.lastName}</span>}
                        </div>
                      </div>
                      <div className="auth-form__field">
                        <label htmlFor="adminPhone" className="auth-form__label">Admin Phone Number</label>
                        <div className="auth-form__phone-wrapper">
                          <span className="auth-form__phone-prefix">+1</span>
                          <input type="tel" id="adminPhone" name="adminPhone" placeholder="(555) 000-0000" value={formData.adminPhone} onChange={handleChange} className="auth-form__input auth-form__input--phone" />
                        </div>
                      </div>
                      <label className="auth-form__checkbox-label auth-form__sms-label">
                        <div className="auth-form__checkbox-wrapper" style={{ flexShrink: 0, marginTop: 2 }}>
                          <input type="checkbox" name="smsOptIn" checked={formData.smsOptIn} onChange={handleChange} className="auth-form__checkbox" />
                          <svg className="auth-form__checkbox-icon" viewBox="0 0 12 10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1.5 5 4.5 8 10.5 2" /></svg>
                        </div>
                        <span className="auth-form__checkbox-text auth-form__sms-text">
                          I agree to receive recurring SMS text messages from Ogiso Health Inc. at the number provided, including account notifications, scheduling, and client success communications. Msg &amp; data rates may apply. Message frequency varies. Reply HELP for help and STOP to cancel. View our{' '}
                          <Link to="/privacy-policy" className="auth-form__link">Privacy Policy</Link>{' '}and{' '}
                          <Link to="/term-conditions" className="auth-form__link">Terms of Service</Link>.
                        </span>
                      </label>
                      <div className="auth-form__field">
                        <label htmlFor="adminEmail" className="auth-form__label">Admin Email</label>
                        <input type="email" id="adminEmail" name="email" placeholder="john@example.com" value={formData.email} onChange={handleChange} className={`auth-form__input ${errors.email ? 'auth-form__input--error' : ''}`} />
                        {errors.email && <span className="auth-form__error">{errors.email}</span>}
                      </div>
                      <div className="auth-form__field">
                        <label htmlFor="signupPassword" className="auth-form__label">Password</label>
                        <div className="auth-form__input-wrapper">
                          <input type={showPassword ? 'text' : 'password'} id="signupPassword" name="password" placeholder="••••••••" value={formData.password} onChange={handleChange} className={`auth-form__input ${errors.password ? 'auth-form__input--error' : ''}`} />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="auth-form__toggle-password">{showPassword ? <EyeOffIcon /> : <EyeIcon />}</button>
                        </div>
                        {errors.password && <span className="auth-form__error">{errors.password}</span>}
                      </div>
                      <div className="auth-form__field">
                        <label htmlFor="confirmPassword" className="auth-form__label">Re-enter Password</label>
                        <div className="auth-form__input-wrapper">
                          <input type={showPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword" placeholder="••••••••" value={formData.confirmPassword} onChange={handleChange} className={`auth-form__input ${errors.confirmPassword ? 'auth-form__input--error' : ''}`} />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="auth-form__toggle-password">{showPassword ? <EyeOffIcon /> : <EyeIcon />}</button>
                        </div>
                        {errors.confirmPassword && <span className="auth-form__error">{errors.confirmPassword}</span>}
                      </div>
                      <div className="auth-form__captcha">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={RECAPTCHA_SITE_KEY}
                          onChange={(token) => { setCaptchaToken(token); setErrors((prev) => ({ ...prev, captcha: '' })); }}
                          onExpired={() => setCaptchaToken(null)}
                        />
                        {errors.captcha && <span className="auth-form__error">{errors.captcha}</span>}
                      </div>
                      <div className="auth-form__google-wrapper">
                        <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => {}} size="large" text="" logo_alignment="center" shape="rectangular" />
                      </div>
                    </>
                  )}
                  {step === 2 && (
                    <>
                      <div className="auth-form__field">
                        <label htmlFor="businessName" className="auth-form__label">Business Name</label>
                        <input type="text" id="businessName" name="businessName" placeholder="Acme Corporation" value={formData.businessName} onChange={handleChange} className={`auth-form__input ${errors.businessName ? 'auth-form__input--error' : ''}`} />
                        {errors.businessName && <span className="auth-form__error">{errors.businessName}</span>}
                      </div>
                      <div className="auth-form__field">
                        <label htmlFor="businessEmail" className="auth-form__label">Business Email</label>
                        <input type="email" id="businessEmail" name="businessEmail" placeholder="contact@acme.com" value={formData.businessEmail} onChange={handleChange} className={`auth-form__input ${errors.businessEmail ? 'auth-form__input--error' : ''}`} />
                        {errors.businessEmail && <span className="auth-form__error">{errors.businessEmail}</span>}
                      </div>
                      <div className="auth-form__field">
                        <label htmlFor="businessPhone" className="auth-form__label">Business Phone Number</label>
                        <div className="auth-form__phone-wrapper">
                          <span className="auth-form__phone-prefix">+1</span>
                          <input type="tel" id="businessPhone" name="businessPhone" placeholder="(555) 000-0000" value={formData.businessPhone} onChange={handleChange} className="auth-form__input auth-form__input--phone" />
                        </div>
                      </div>
                    </>
                  )}
                  {step === 3 && (
                    <>
                      <div className={`auth-form__facility-fields ${formData.skipFacility ? 'auth-form__facility-fields--disabled' : ''}`}>
                        <div className="auth-form__field">
                          <label htmlFor="facilityName" className="auth-form__label">Facility Name</label>
                          <input type="text" id="facilityName" name="facilityName" placeholder="Facility Name" value={formData.facilityName} onChange={handleChange} disabled={formData.skipFacility} className={`auth-form__input ${errors.facilityName ? 'auth-form__input--error' : ''}`} />
                          {errors.facilityName && <span className="auth-form__error">{errors.facilityName}</span>}
                        </div>
                        <div className="auth-form__field">
                          <label htmlFor="facilityType" className="auth-form__label">Facility Type</label>
                          <div className="auth-form__select-wrapper">
                            <select id="facilityType" name="facilityType" value={formData.facilityType} onChange={handleChange} disabled={formData.skipFacility} className={`auth-form__select ${errors.facilityType ? 'auth-form__select--error' : ''}`}>
                              <option value="">Select Facility Type</option>
                              {facilityTypeOptions.map((ft) => (
                                <option key={ft.id || ft._id} value={ft.id || ft._id}>{ft.name}</option>
                              ))}
                            </select>
                            <span className="auth-form__select-icon"><ChevronDownIcon /></span>
                          </div>
                          {errors.facilityType && <span className="auth-form__error">{errors.facilityType}</span>}
                        </div>
                        <div className="auth-form__field">
                          <label htmlFor="facilityAddress" className="auth-form__label">Address</label>
                          <input type="text" id="facilityAddress" name="facilityAddress" placeholder="123 Main Street" value={formData.facilityAddress} onChange={handleChange} disabled={formData.skipFacility} className={`auth-form__input ${errors.facilityAddress ? 'auth-form__input--error' : ''}`} />
                          {errors.facilityAddress && <span className="auth-form__error">{errors.facilityAddress}</span>}
                        </div>
                        <div className="auth-form__field">
                          <label htmlFor="facilityApt" className="auth-form__label">Address 2 (Optional)</label>
                          <input type="text" id="facilityApt" name="facilityApt" placeholder="Apt/Suite #" value={formData.facilityApt} onChange={handleChange} disabled={formData.skipFacility} className="auth-form__input" />
                        </div>
                        <div className="auth-form__row">
                          <div className="auth-form__field">
                            <label htmlFor="facilityZip" className="auth-form__label">Zip Code</label>
                            <input type="text" id="facilityZip" name="facilityZip" placeholder="23464" value={formData.facilityZip} onChange={handleChange} disabled={formData.skipFacility} className={`auth-form__input ${errors.facilityZip ? 'auth-form__input--error' : ''}`} />
                            {errors.facilityZip && <span className="auth-form__error">{errors.facilityZip}</span>}
                          </div>
                          <div className="auth-form__field">
                            <label htmlFor="facilityState" className="auth-form__label">State</label>
                            <div className="auth-form__select-wrapper">
                              <select id="facilityState" name="facilityState" value={formData.facilityState} onChange={handleChange} disabled={formData.skipFacility} className={`auth-form__select ${errors.facilityState ? 'auth-form__select--error' : ''}`}>
                                <option value="">Select State</option>
                                {usStates.map((s) => (
                                  <option key={s.abbreviation} value={s.abbreviation}>{s.name}</option>
                                ))}
                              </select>
                              <span className="auth-form__select-icon"><ChevronDownIcon /></span>
                            </div>
                            {errors.facilityState && <span className="auth-form__error">{errors.facilityState}</span>}
                          </div>
                        </div>
                      </div>
                      <label className="auth-form__checkbox-label auth-form__skip-label">
                        <div className="auth-form__checkbox-wrapper">
                          <input type="checkbox" name="skipFacility" checked={formData.skipFacility} onChange={handleChange} className="auth-form__checkbox" />
                          <svg className="auth-form__checkbox-icon" viewBox="0 0 12 10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1.5 5 4.5 8 10.5 2" /></svg>
                        </div>
                        <span className="auth-form__checkbox-text">Skip for now</span>
                      </label>
                    </>
                  )}
                  <p className="auth-form__terms">
                    By clicking on the Confirm button, you are agreeing to our{' '}
                    <Link to="/term-conditions" className="auth-form__link">Terms &amp; Conditions</Link>{' '}and{' '}
                    <Link to="/privacy-policy" className="auth-form__link">Privacy Policy</Link>.
                  </p>
                  <div className="auth-form__nav">
                    <button type="button" onClick={handlePrevStep} className="auth-form__nav-back">
                      {step === 1 || (step === 2 && fromVerification) ? 'Back to Login' : 'Back'}
                    </button>
                    <button type="button" onClick={step === 3 ? handleCreateAccount : handleNextStep} disabled={isSubmitting} className="auth-form__nav-next">
                      {isSubmitting ? <><span className="auth-form__spinner" />Processing...</> : step === 3 ? 'Create Account' : 'Confirm'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
    </>
  );
};

export default AuthPage;
